import Controller from '@ember/controller';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';

import { shouldSkipKyc } from '@repo/identity-verification-kit/utils/should-skip-kyc';

import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { StepListWrapper } from 'register-partners-js/react/components/step-list';
import {
  getDougsDisplayName,
  isDougsCobrandingEnabled,
} from 'register-partners-js/utils/is-cobranding-dougs';

import styles from './styles.module.css';

export default class RegistrationStartController extends Controller {
  mainLayout = MainLayout;

  stepList = StepListWrapper;

  @service intl;
  @service segment;
  @service router;

  get legalPartnerName() {
    return capitalize(this.model.registration.partnerName);
  }

  get isPartnerTypeAccountant() {
    return this.model.registration.partnerType === 'accountant';
  }

  get companyCreationFromScratch() {
    return this.model.registration.companyCreationFromScratch;
  }

  get title() {
    if (this.companyCreationFromScratch) {
      return this.intl.t('user-creation-success.fr-full-cc.title');
    }
    if (this.model.registration.partnerType === 'accountant') {
      return this.intl.t('user-creation-success.accountant.title', { htmlSafe: true });
    } else if (isDougsCobrandingEnabled(this.model.registration.partnerName)) {
      return this.intl.t('user-creation-success.partner.title', {
        partner: getDougsDisplayName(this.model.registration.partnerName),
      });
    } else {
      return this.intl.t('user-creation-success.title', {
        legal_tech_partner_name: this.legalPartnerName,
        htmlSafe: true,
      });
    }
  }

  get shouldShowKycStep() {
    return this.model.kycInfo ? !shouldSkipKyc(this.model.kycInfo) : true;
  }

  get stepOne() {
    if (this.companyCreationFromScratch) {
      return {
        title: this.intl.t('user-creation-success.fr-full-cc.item1.title'),
        subtitle: this.intl.t('user-creation-success.fr-full-cc.item1.subtitle'),
      };
    } else if (isDougsCobrandingEnabled(this.model.registration.partnerName)) {
      return {
        title: this.intl.t('user-creation-success.partner.item.1.title', {
          partner: getDougsDisplayName(this.model.registration.partnerName),
        }),
        subtitle: this.intl.t('user-creation-success.item.1.subtitle'),
      };
    } else if (this.shouldShowKycStep) {
      return {
        title: this.intl.t('user-creation-success.item.1.title'),
        subtitle: this.intl.t('user-creation-success.item.1.subtitle'),
      };
    } else if (!this.shouldShowKycStep) {
      return {
        title: this.intl.t('user-creation-success.item.1.title'),
        subtitle: this.intl.t('user-creation-success.item.1.subtitle-skip-kyc'),
      };
    }

    return null;
  }

  get stepTwo() {
    if (this.companyCreationFromScratch && this.shouldShowKycStep) {
      return {
        title: this.intl.t('user-creation-success.fr-full-cc.item2.title'),
        subtitle: this.intl.t('user-creation-success.fr-full-cc.item2.subtitle'),
      };
    } else {
      return {
        title: this.intl.t('user-creation-success.item.2.title'),
        subtitle: this.isPartnerTypeAccountant
          ? this.intl.t('user-creation-success.accountant.item.2.subtitle')
          : this.intl.t('user-creation-success.item.2.subtitle'),
      };
    }
  }

  get stepThree() {
    if (this.companyCreationFromScratch) {
      return {
        title: this.intl.t('user-creation-success.fr-full-cc.item3.title'),
        subtitle: this.intl.t('user-creation-success.fr-full-cc.item3.subtitle'),
      };
    } else {
      return {
        title: this.intl.t('user-creation-success.item.3.title'),
        subtitle: this.isPartnerTypeAccountant
          ? this.intl.t('user-creation-success.accountant.item.3.subtitle')
          : this.intl.t('user-creation-success.item.3.subtitle', {
              legal_tech_partner_name: this.legalPartnerName,
            }),
      };
    }
  }

  get stepFour() {
    if (this.companyCreationFromScratch) {
      return {
        title: this.intl.t('user-creation-success.fr-full-cc.item4.title'),
        subtitle: this.intl.t('user-creation-success.fr-full-cc.item4.subtitle'),
      };
    } else if (isDougsCobrandingEnabled(this.model.registration.partnerName)) {
      return {
        title: this.intl.t('user-creation-success.partner.item.4.title', {
          partner: getDougsDisplayName(this.model.registration.partnerName),
        }),
        subtitle: this.intl.t('user-creation-success.item.4.subtitle'),
      };
    } else {
      return {
        title: this.intl.t('user-creation-success.item.4.title'),
        subtitle: this.isPartnerTypeAccountant
          ? this.intl.t('user-creation-success.accountant.item.4.subtitle')
          : this.intl.t('user-creation-success.item.4.subtitle'),
      };
    }
  }

  get stepListSteps() {
    let steps = [this.stepOne, this.stepTwo, this.stepThree, this.stepFour].filter(Boolean);

    return steps.map((step, index) => ({
      className: this.companyCreationFromScratch ? 'mb-32' : '',
      title: step.title,
      subtitle: step.subtitle,
      [`data-test-user-creation-success-step-${index + 1}`]: '',
    }));
  }

  get button() {
    if (this.model.registration.companyCreationFromScratch) {
      return this.intl.t('generic.continue.cta');
    } else return this.intl.t('user-creation-success.cta');
  }

  styles = styles;
}
